// src/services/noteDeFraisStorage.js

import apiService from './apiService';

const DB_NAME = 'NoteDeFraisDB';
const DB_VERSION = 1;
const STORE_NAME = 'notes_frais';

class NoteDeFraisStorage {
  constructor() {
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('inspecteur_id', 'inspecteur_id', { unique: false });
          store.createIndex('statut', 'statut', { unique: false });
          store.createIndex('date_depense', 'date_depense', { unique: false });
          store.createIndex('synced', 'synced', { unique: false });
        }
      };
    });
  }

  async ensureDB() {
    if (!this.db) {
      await this.init();
    }
  }

  generateId() {
    return `note_local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async saveNote(noteData) {
    await this.ensureDB();
    
    const note = {
      id: noteData.id || this.generateId(),
      inspecteur_id: noteData.inspecteur_id,
      inspecteur_nom: noteData.inspecteur_nom,
      date_depense: noteData.date_depense,
      categorie: noteData.categorie,
      description: noteData.description,
      montant: parseFloat(noteData.montant),
      tva: noteData.tva != null ? parseFloat(noteData.tva) : null,
      taux_tva: noteData.taux_tva != null ? parseFloat(noteData.taux_tva) : null,
      statut: noteData.statut || 'brouillon',
      ticket_photo_base64: noteData.ticket_photo_base64 || null, // null si image compressée (pas de base64)
      ticket_photo_url: noteData.ticket_photo_url || noteData.justificatif || null,
      mois_comptable: noteData.mois_comptable,
      trajet: noteData.trajet,
      client_visite: noteData.client_visite,
      synced: noteData.synced || false,
      base44_id: noteData.base44_id || null,
      created_at: noteData.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(note);

      request.onsuccess = () => resolve(note);
      request.onerror = () => reject(request.error);
    });
  }

  async getNote(id) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllNotes(inspecteurId = null) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      let request;
      
      if (inspecteurId !== null && inspecteurId !== undefined && inspecteurId !== '') {
        console.log('🔍 Récupération notes pour inspecteur:', inspecteurId);
        const index = store.index('inspecteur_id');
        request = index.getAll(inspecteurId);
      } else {
        console.log('🔍 Récupération de TOUTES les notes (pas d\'inspecteur spécifié)');
        request = store.getAll();
      }

      request.onsuccess = () => {
        const notes = request.result.sort((a, b) => 
          new Date(b.date_depense) - new Date(a.date_depense)
        );
        console.log(`✅ ${notes.length} notes récupérées`);
        resolve(notes);
      };
      request.onerror = () => {
        console.error('❌ Erreur getAllNotes:', request.error);
        reject(request.error);
      };
    });
  }

  async deleteNote(id) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getUnsyncedNotes() {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        // ✅ CORRECTION: Filtrer manuellement au lieu d'utiliser index.getAll(false)
        const unsyncedNotes = request.result.filter(note => !note.synced);
        console.log(`🔍 ${unsyncedNotes.length} notes non synchronisées trouvées`);
        resolve(unsyncedNotes);
      };
      request.onerror = () => {
        console.error('❌ Erreur getUnsyncedNotes:', request.error);
        reject(request.error);
      };
    });
  }

  async markAsSynced(id, base44Id = null) {
    await this.ensureDB();
    
    const note = await this.getNote(id);
    if (note) {
      note.synced = true;
      if (base44Id) {
        note.base44_id = base44Id;
      }
      await this.saveNote(note);
    }
  }

  async syncToBase44() {
    try {
      const unsyncedNotes = await this.getUnsyncedNotes();
      const results = [];

      console.log(`🔄 Sync de ${unsyncedNotes.length} notes non synchronisées...`);

      for (const note of unsyncedNotes) {
        try {
          console.log(`📤 Sync note ${note.id}...`);

          const fresh = await this.getNote(note.id);
          const localNote = fresh || note;

          const resolved = await this.resolveTicketPhotoUrl(localNote);
          if (!resolved.success) {
            console.error(`❌ Justificatif note ${note.id}:`, resolved.error);
            results.push({ success: false, localId: note.id, error: resolved.error });
            continue;
          }

          const noteData = {
            date_depense: localNote.date_depense,
            categorie: localNote.categorie,
            description: localNote.description,
            montant: localNote.montant,
            tva: localNote.tva ?? null,
            taux_tva: localNote.taux_tva ?? null,
            ticket_photo_url: resolved.ticket_photo_url || null,
            mois_comptable: localNote.mois_comptable,
            trajet: localNote.trajet || null,
            client_visite: localNote.client_visite || null,
            statut: localNote.statut,
            km_parcourus: localNote.km_parcourus,
          };

          if (localNote.inspecteur_id) {
            noteData.inspecteur_id = localNote.inspecteur_id;
          }

          console.log('📦 Envoi API notes-de-frais:', {
            ...noteData,
            ticket_photo_url: noteData.ticket_photo_url || '(aucun)',
          });

          let result;
          if (localNote.base44_id) {
            result = await apiService.updateNoteDeFrais(localNote.base44_id, noteData);
          } else {
            result = await apiService.createNoteDeFrais(noteData);
          }

          console.log('📥 Réponse Base44:', result);

          if (result && result.success) {
            const base44Id = result.note?.id || result.note_id;
            await this.markAsSynced(localNote.id, base44Id);
            console.log(`✅ Note ${localNote.id} synchronisée (serveur id: ${base44Id})`);
            results.push({ success: true, localId: localNote.id, base44Id: base44Id });
          } else {
            console.error(`❌ Échec sync note ${localNote.id}:`, result?.error || 'Réponse invalide');
            results.push({ success: false, localId: localNote.id, error: result?.error || 'Réponse invalide' });
          }
        } catch (error) {
          console.error(`❌ Exception sync note ${note.id}:`, error);
          console.error('Stack:', error.stack);
          results.push({ success: false, localId: note.id, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      console.log(`📊 Résumé sync: ${successCount} réussies, ${failCount} échouées`);
      
      if (failCount > 0) {
        console.log('❌ Détails des échecs:', results.filter(r => !r.success));
      }

      return {
        success: failCount === 0,
        synced: successCount,
        failed: failCount,
        details: results
      };
    } catch (error) {
      console.error('❌ ERREUR SYNC GLOBALE:', error.message);
      return {
        success: false,
        error: error.message,
        synced: 0,
        failed: 0
      };
    }
  }

  async syncFromBase44(inspecteurId) {
    try {
      const response = await apiService.listNotesDeFrais({
        inspecteurId: inspecteurId,
      });
      
      if (!response.success) {
        throw new Error(response.error || 'Erreur récupération notes');
      }
      
      const base44Notes = response.notes || [];

      for (const base44Note of base44Notes) {
        const localNote = {
          id: `base44_${base44Note.id}`,
          base44_id: base44Note.id,
          inspecteur_id: base44Note.inspecteur_id,
          inspecteur_nom: base44Note.inspecteur_nom,
          date_depense: base44Note.date_depense,
          categorie: base44Note.categorie,
          description: base44Note.description,
          montant: base44Note.montant,
          statut: base44Note.statut,
          ticket_photo_url: base44Note.ticket_photo_url,
          mois_comptable: base44Note.mois_comptable,
          trajet: base44Note.trajet,
          client_visite: base44Note.client_visite,
          synced: true,
          created_at: base44Note.created_at,
          updated_at: base44Note.updated_at
        };

        await this.saveNote(localNote);
      }

      return {
        success: true,
        imported: base44Notes.length
      };
    } catch (error) {
      console.error('❌ Erreur import depuis API:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async deleteFromBase44(noteId) {
    try {
      const note = await this.getNote(noteId);
      
      if (note && note.base44_id) {
        await apiService.deleteNoteDeFrais(note.base44_id);
      }

      await this.deleteNote(noteId);

      return { success: true };
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      return { success: false, error: error.message };
    }
  }

  base64ToFile(base64String, filename) {
    const arr = base64String.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Convertit une image ticket (data URL ou base64 brut) en File pour upload multipart.
   */
  base64ToTicketFile(base64String) {
    if (!base64String || typeof base64String !== 'string') return null;
    const s = base64String.trim();
    try {
      if (s.startsWith('data:')) {
        const arr = s.split(',');
        if (arr.length < 2) return null;
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        const ext = mime.includes('png')
          ? 'png'
          : mime === 'application/pdf'
            ? 'pdf'
            : 'jpg';
        return new File([u8arr], `ticket.${ext}`, { type: mime });
      }
      const bstr = atob(s);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) u8arr[n] = bstr.charCodeAt(n);
      return new File([u8arr], 'ticket.jpg', { type: 'image/jpeg' });
    } catch (e) {
      console.error('base64ToTicketFile:', e);
      return null;
    }
  }

  /**
   * Avant sync : upload du ticket si stocké en base64 local (l'API n'accepte que ticket_photo_url).
   */
  async resolveTicketPhotoUrl(note) {
    let url = note.ticket_photo_url || null;
    if (url) return { success: true, ticket_photo_url: url };

    if (!note.ticket_photo_base64) {
      return { success: true, ticket_photo_url: null };
    }

    const file = this.base64ToTicketFile(note.ticket_photo_base64);
    if (!file) {
      return { success: false, error: 'Image ticket illisible (base64 invalide)' };
    }

    const upload = await apiService.uploadNoteDeFraisTicket(file);
    if (!upload.success) {
      return { success: false, error: upload.error || 'Échec upload du justificatif' };
    }

    url = upload.file_url;
    const updated = { ...note, ticket_photo_url: url };
    await this.saveNote(updated);
    return { success: true, ticket_photo_url: url };
  }
}

const noteDeFraisStorage = new NoteDeFraisStorage();
export default noteDeFraisStorage;